import React, { useMemo, useCallback } from 'react';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { useInfiniteCourses, CoursesFilters } from '@/hooks/queries/useCourses';
import { useTranslation } from '@/hooks/useTranslation';
import CourseCard from './CourseCard';
import CourseCardSkeleton from './CourseCardSkeleton';
import CoursesEmptyState from './CoursesEmptyState';
import CoursesErrorState from './CoursesErrorState';

interface CoursesContentProps {
  filters: CoursesFilters;
  selectedLanguage: string;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
}

const CoursesContent: React.FC<CoursesContentProps> = ({
  filters,
  selectedLanguage,
  hasActiveFilters,
  onClearFilters,
}) => {
  const { t } = useTranslation('courses');

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, error } = useInfiniteCourses(filters);

  const courses = useMemo(() => {
    return (
      data?.pages.flatMap((page) => {
        return page.data || [];
      }) || []
    );
  }, [data]);

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const loaderRef = useInfiniteScroll({
    hasMore: !!hasNextPage && !isFetchingNextPage,
    onLoadMore: handleLoadMore,
  });

  return (
    <div className="flex-1">
      {/* Error State */}
      {error && (
        <CoursesErrorState error={error.message || t('states.error.message')} onRetry={() => window.location.reload()} />
      )}

      {/* Loading State */}
      {isLoading && courses.length === 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 lg:gap-8">
          {Array.from({ length: 6 }).map((_, i) => (
            <CourseCardSkeleton key={i} />
          ))}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && courses.length === 0 && !error && (
        <CoursesEmptyState hasActiveFilters={hasActiveFilters} onClearFilters={onClearFilters} />
      )}

      {/* Courses Grid */}
      {courses.length > 0 && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 lg:gap-8">
            {courses.map((course) => (
              <CourseCard
                key={course.id}
                course={course}
                language={selectedLanguage === 'all' ? 'en' : selectedLanguage}
              />
            ))}
          </div>

          {/* Infinite Scroll Loader */}
          {hasNextPage && (
            <div ref={loaderRef} className="mt-12">
              {isFetchingNextPage && (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 lg:gap-8">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <CourseCardSkeleton key={`skeleton-${i}`} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* End of Results */}
          {!hasNextPage && courses.length > 0 && (
            <div className="mt-12 text-center">
              <div className="inline-flex items-center gap-3 px-6 py-3 bg-white/60 dark:bg-gray-900/60 backdrop-blur-xl rounded-full border border-gray-200/50 dark:border-gray-800/50 shadow-lg">
                <div className="w-2 h-2 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500" />
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  {t('states.endOfResults')}
                </span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CoursesContent;

